import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { blogData } from '../../mocks/blog';
import SeoHead from '../../components/SeoHead';

const categories = ['All', 'Compliance', 'Crypto', 'Tax', 'KYC', 'iGaming'];

const resourcesJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Resources & Insights | CJ Solutions Ltd',
    url: `${import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com'}/resources`,
    description: 'Expert articles on compliance, AML, iGaming licensing, Forex regulation, tax, and KYC — written by the CJ Solutions Ltd specialist team in Cyprus.',
    publisher: {
      '@type': 'Organization',
      name: 'CJ Solutions Ltd',
      url: `${import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com'}`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com'}` },
      { '@type': 'ListItem', position: 2, name: 'Resources & Insights', item: `${import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com'}/resources` },
    ],
  },
];

export default function ResourcesPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = blogData.filter((article) => {
    const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featured = blogData[0];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Compliance & Licensing Insights | CJ Solutions"
        description="Expert articles on compliance, AML, iGaming licensing, Forex regulation, tax, and KYC written by the CJ Solutions Ltd specialist team in Limassol, Cyprus."
        keywords="compliance articles, AML insights, iGaming licensing blog, Forex regulation, KYC resources, Cyprus tax"
        canonical="/resources"
        jsonLd={resourcesJsonLd}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1e3a66c2ec4ae728235d027ae73f3be0.jpg"
            alt="Resources hero"
            width={1600}
            height={700}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/50 to-slate-900/80"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-amber-500/20 border border-amber-500/40 text-amber-400 text-sm font-semibold uppercase tracking-widest rounded mb-6">
            Knowledge Hub
          </span>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-tight mb-6">
            Resources &amp; Insights
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Expert articles on compliance, licensing, tax, and regulation — written by our team of specialists.
          </p>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="sticky top-0 z-40 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {activeCategory === 'All' && searchQuery === '' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-6">Featured Article</p>
          <div
            onClick={() => navigate(`/resources/${featured.slug}`)}
            className="group grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl border border-slate-100 cursor-pointer hover:shadow-2xl transition-all duration-300"
          >
            <div className="relative h-72 lg:h-auto overflow-hidden">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute top-4 left-4">
                <span className="inline-block px-3 py-1 bg-white text-slate-900 text-xs font-semibold rounded-full">
                  {featured.category}
                </span>
              </div>
            </div>
            <div className="bg-white p-10 flex flex-col justify-center">
              <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{featured.date}</span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i>{featured.readTime}</span>
              </div>
              <h2 className="text-3xl font-semibold text-slate-900 mb-4 group-hover:text-amber-500 transition-colors leading-snug">
                {featured.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">{featured.excerpt}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-slate-900 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {featured.author.charAt(0)}
                  </div>
                  <span className="text-sm text-slate-700 font-medium">{featured.author}</span>
                </div>
                <span className="text-amber-500 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all whitespace-nowrap">
                  Read More <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-article-line text-5xl text-slate-300 mb-4 block"></i>
            <p className="text-slate-500 text-lg">No articles found. Try a different search or category.</p>
          </div>
        ) : (
          <>
            {(activeCategory !== 'All' || searchQuery !== '') && (
              <p className="text-slate-500 text-sm mb-8">{filtered.length} article{filtered.length !== 1 ? 's' : ''} found</p>
            )}
            {activeCategory === 'All' && searchQuery === '' && (
              <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-6">All Articles</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((article) => (
                <article
                  key={article.slug}
                  onClick={() => navigate(`/resources/${article.slug}`)}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-slate-100"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1 bg-white text-slate-900 text-xs font-semibold rounded-full">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3 group-hover:text-amber-500 transition-colors line-clamp-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                          {article.author.charAt(0)}
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{article.author}</span>
                      </div>
                      <span className="text-amber-500 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all whitespace-nowrap text-sm">
                        Read More <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      {/* CTA */}
      <section className="bg-slate-50 border-t border-slate-100 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-slate-900 mb-4">Have a specific question?</h2>
          <p className="text-slate-600 text-lg mb-8">Our compliance and licensing experts are ready to help. Book a free consultation today.</p>
          <Link
            to="/#contact"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-block px-8 py-4 bg-amber-500 text-white text-lg font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Book a Free Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
